import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../../core/services/api.service';
import { MultiSelectComponent, SelectOption } from '../../../shared/components/multi-select/multi-select.component';
import { AuthorMasterComponent } from '../author-master/author-master.component';
import { isbnValidator, yearValidator } from '../../../shared/validators/custom-validators';
import { autoEnglishName } from '../../../core/utils/transliterate.util';
import { forkJoin, of, Subscription } from 'rxjs';

export interface BookSavedEvent {
  result: any;
  bookName: string;
}

@Component({
  selector: 'app-book-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, MultiSelectComponent, AuthorMasterComponent],
  templateUrl: './book-form.component.html',
  styleUrl: './book-form.component.scss',
})
export class BookFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() editBook: any | null = null;
  @Output() saved = new EventEmitter<BookSavedEvent>();

  form!: FormGroup;
  isSaving = false;
  errorMessage = '';
  successMessage = '';

  authorOptions: SelectOption[] = [];
  categoryOptions: SelectOption[] = [];
  readonly currentYear = new Date().getFullYear();

  showClassModal = false;
  classModalType: 'authors' | 'categories' | 'original-authors' = 'authors';
  classModalAuthors: string[] = [];
  classModalCategories: string[] = [];
  classModalOriginalAuthors: string[] = [];
  lastAddedAuthorName = '';

  showAuthorOverlay = false;

  duplicates: any[] = [];
  isDuplicateChecking = false;
  showDuplicates = false;

  private _overlaySourceType: 'authors' | 'original-authors' = 'authors';
  private _translatedSub?: Subscription;

  get isEditMode(): boolean { return this.editBook != null; }
  get f() { return this.form.controls; }

  constructor(private fb: FormBuilder, private api: ApiService) {}

  ngOnInit(): void {
    this.buildForm();
    this._loadMasterData();
    if (this.editBook) this._patchForm(this.editBook);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) return;
    if (changes['editBook']) {
      if (this.editBook) {
        this._patchForm(this.editBook);
      } else {
        this.resetForm();
      }
    }
  }

  ngOnDestroy(): void { this._translatedSub?.unsubscribe(); }

  private buildForm(): void {
    this.form = this.fb.group({
      bookName:            ['', [Validators.required, Validators.minLength(5), Validators.maxLength(200)]],
      bookNameEnglish:     ['', [Validators.maxLength(200)]],
      authorNames:         [[], []],
      ISBN:                ['', [isbnValidator()]],
      price:               [null, [Validators.min(0)]],
      copiesInField:       [{ value: 0, disabled: true }],
      isTranslated:        [false],
      categoryNames:       [[], []],
      firstPublishedIn:    [null, [yearValidator(1000, this.currentYear)]],
      originalBookName:    ['', [Validators.maxLength(200)]],
      originalLanguage:    ['', [Validators.maxLength(100)]],
      originalAuthorNames: [[], []],
    });

    this._translatedSub = this.form.get('isTranslated')!.valueChanges.subscribe((val: boolean) => {
      const n = this.form.get('originalBookName')!;
      const l = this.form.get('originalLanguage')!;
      if (val) {
        n.setValidators([Validators.required, Validators.maxLength(200)]);
        l.setValidators([Validators.required, Validators.maxLength(100)]);
      } else {
        n.clearValidators();
        l.clearValidators();
      }
      n.updateValueAndValidity();
      l.updateValueAndValidity();
    });
  }

  /** The native name is always shown; the English name is appended only when it differs, and is searchable either way. */
  private _mapNamedOptions(list: any[], nameField: string, englishNameField: string): SelectOption[] {
    return (list || []).map((x: any) => {
      const name = x[nameField];
      const englishName = x[englishNameField];
      const hasDistinctEnglishName = englishName && englishName.trim() && englishName.trim() !== name.trim();
      return {
        label: hasDistinctEnglishName ? `${name} (${englishName})` : name,
        value: name,
        searchText: hasDistinctEnglishName ? englishName : undefined,
      };
    });
  }

  private _mapAuthorOptions(list: any[]): SelectOption[] {
    return this._mapNamedOptions(list, 'authorName', 'authorEnglishName');
  }

  private _mapCategoryOptions(list: any[]): SelectOption[] {
    return this._mapNamedOptions(list, 'categoryName', 'categoryNameEnglish');
  }

  private _loadMasterData(): void {
    this.api.getAuthors({ isActive: 'true' }).subscribe({
      next: (a) => { this.authorOptions = this._mapAuthorOptions(a); },
    });
    this.api.getCategories({ isActive: 'true' }).subscribe({
      next: (c) => { this.categoryOptions = this._mapCategoryOptions(c); },
    });
  }

  private _patchForm(book: any): void {
    this.form.patchValue({
      bookName:            book.bookName,
      bookNameEnglish:     book.bookNameEnglish || '',
      authorNames:         book.authorNames || [],
      ISBN:                book.ISBN || '',
      price:               book.price ?? null,
      copiesInField:       book.copiesInField || 0,
      isTranslated:        book.isTranslated || false,
      originalBookName:    book.originalBookName || '',
      originalLanguage:    book.originalLanguage || '',
      originalAuthorNames: book.originalAuthorNames || [],
      categoryNames:       book.categoryNames || [],
      firstPublishedIn:    book.firstPublishedIn ?? null,
    });
    this.errorMessage = '';
    this.successMessage = '';
    this.duplicates = [];
    this.showDuplicates = false;
  }

  resetForm(): void {
    this.form.reset({
      bookNameEnglish: '', copiesInField: 0, isTranslated: false,
      authorNames: [], originalBookName: '', originalLanguage: '',
      originalAuthorNames: [], categoryNames: [], firstPublishedIn: null,
    });
    this.errorMessage = '';
    this.successMessage = '';
    this.duplicates = [];
    this.showDuplicates = false;
  }

  copyBookNameToEnglish(): void {
    this.form.get('bookNameEnglish')!.setValue(this.form.get('bookName')!.value || '');
  }

  isFieldInvalid(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  // ── Duplicate check ──────────────────────────────────────────────
  onBookNameInput(): void {
    if (!(this.form.get('bookName')!.value || '').trim()) {
      this.showDuplicates = false;
      this.duplicates = [];
      this.isDuplicateChecking = false;
    }
  }

  onBookNameBlur(): void {
    const name = (this.form.get('bookName')!.value || '').trim();
    if (name) {
      const suggested = autoEnglishName(name);
      if (suggested) this.form.get('bookNameEnglish')!.setValue(suggested);
    }
    this._checkDuplicatesForBookName();
  }

  private _checkDuplicatesForBookName(): void {
    const bookName    = (this.form.get('bookName')!.value || '').trim();
    const englishName = (this.form.get('bookNameEnglish')!.value || '').trim();
    this.showDuplicates = false;
    this.duplicates = [];
    if (!bookName || bookName.length < 2) return;
    this.isDuplicateChecking = true;
    forkJoin([
      this.api.searchBooks(bookName, 'bookName'),
      englishName ? this.api.searchBooks(englishName, 'bookNameEnglish') : of([]),
    ]).subscribe({
      next: ([r1, r2]) => {
        const seen = new Set<string>();
        const merged: any[] = [];
        for (const b of [...(r1 || []), ...(r2 || [])]) {
          const key = b._id || b.bookId;
          if (key && !seen.has(key)) { seen.add(key); merged.push(b); }
        }
        this.duplicates = merged.slice(0, 5);
        this.showDuplicates = this.duplicates.length > 0;
        this.isDuplicateChecking = false;
      },
      error: () => { this.isDuplicateChecking = false; },
    });
  }

  onEnglishNameBlur(): void {
    this._checkDuplicates((this.form.get('bookNameEnglish')!.value || '').trim(), 'bookNameEnglish');
  }

  private _checkDuplicates(name: string, field: string): void {
    this.showDuplicates = false;
    this.duplicates = [];
    if (name.length < 2) return;
    this.isDuplicateChecking = true;
    this.api.searchBooks(name, field).subscribe({
      next: (results) => {
        this.duplicates = (results || []).slice(0, 5);
        this.showDuplicates = this.duplicates.length > 0;
        this.isDuplicateChecking = false;
      },
      error: () => { this.isDuplicateChecking = false; },
    });
  }

  dismissDuplicates(): void { this.showDuplicates = false; }

  // ── Authors / Categories ─────────────────────────────────────────
  removeAuthor(name: string): void {
    const vals: string[] = this.form.get('authorNames')!.value || [];
    this.form.get('authorNames')!.setValue(vals.filter(v => v !== name));
  }

  removeCategory(name: string): void {
    const vals: string[] = this.form.get('categoryNames')!.value || [];
    this.form.get('categoryNames')!.setValue(vals.filter(v => v !== name));
  }

  openClassModal(type: 'authors' | 'categories' | 'original-authors'): void {
    this.classModalType = type;
    if (type === 'authors') {
      this.classModalAuthors = [...(this.form.get('authorNames')!.value || [])];
    } else if (type === 'original-authors') {
      this.classModalOriginalAuthors = [...(this.form.get('originalAuthorNames')!.value || [])];
    } else {
      this.classModalCategories = [...(this.form.get('categoryNames')!.value || [])];
    }
    this.showClassModal = true;
  }

  closeClassModal(): void {
    this.showClassModal = false;
    this.lastAddedAuthorName = '';
  }

  applyClassModal(): void {
    if (this.classModalType === 'authors') {
      this.form.get('authorNames')!.setValue(this.classModalAuthors);
    } else if (this.classModalType === 'original-authors') {
      this.form.get('originalAuthorNames')!.setValue(this.classModalOriginalAuthors);
    } else {
      this.form.get('categoryNames')!.setValue(this.classModalCategories);
    }
    this.showClassModal = false;
  }

  openAuthorOverlay(): void {
    this._overlaySourceType = this.classModalType as 'authors' | 'original-authors';
    this.showClassModal = false;
    this.showAuthorOverlay = true;
  }

  closeAuthorOverlay(): void { this.showAuthorOverlay = false; }

  onAuthorCreated(authorName: string): void {
    this.showAuthorOverlay = false;
    this.lastAddedAuthorName = authorName;
    this.api.getAuthors({ isActive: 'true' }).subscribe({
      next: (a) => {
        this.authorOptions = this._mapAuthorOptions(a);
        this.classModalType = this._overlaySourceType;
        if (this._overlaySourceType === 'original-authors') {
          if (!this.classModalOriginalAuthors.includes(authorName)) {
            this.classModalOriginalAuthors = [...this.classModalOriginalAuthors, authorName];
          }
        } else {
          if (!this.classModalAuthors.includes(authorName)) {
            this.classModalAuthors = [...this.classModalAuthors, authorName];
          }
        }
        this.showClassModal = true;
      },
    });
  }

  // ── Translation fields ───────────────────────────────────────────
  removeOriginalAuthor(name: string): void {
    const current: string[] = this.form.get('originalAuthorNames')!.value || [];
    this.form.get('originalAuthorNames')!.setValue(current.filter(a => a !== name));
  }

  // ── Submit ───────────────────────────────────────────────────────
  onSubmit(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';

    const { copiesInField: _ignored, ...formValues } = this.form.getRawValue();
    const payload = this.isEditMode
      ? { ...formValues, id: this.editBook._id || this.editBook.id }
      : formValues;

    const request$ = this.isEditMode
      ? this.api.updateBook(payload)
      : this.api.saveBook(payload);

    request$.subscribe({
      next: (result: any) => {
        this.isSaving = false;
        this.successMessage = this.isEditMode ? 'Book updated successfully!' : 'Book added successfully!';
        const bookName = (this.form.value.bookName || '').trim();
        this.saved.emit({ result, bookName });
        if (!this.isEditMode) {
          this.resetForm();
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        this.errorMessage = err?.error?.message || 'Failed to save book. Please try again.';
      },
    });
  }
}
